import helpers from '../helpers.js';
import BelongsToMany from './belongsToMany.js';
import APIMessages from './apiMessages.js';

/**
 * @ngdoc service
 * @name BaseClass.BCRestable
 * @description
 * Model extension for REST functionality
 */
export default angular.module('BaseClass.restable',[BelongsToMany, APIMessages])
    .factory('BCRestable', function($q, $injector, $http, $filter, BCBelongsToMany, BCAPIMessages, $log, $state) {
        function NotInitialised() {
            this.name = 'NotInitialised';
            this.message = 'The RESTful interface is not initialised with a API endpoint URL';
        }

        function compileUrl(url, params = {}) {
            var r = /\:([^\/0-9]+)/g;
            let finalUrl = url;
            let urlParams = r.exec(url);
            while(urlParams !== null) {
                let value = params[urlParams[1]];
                if(!value) value = '';
                finalUrl = finalUrl.replace(urlParams[0], value);
                urlParams = r.exec(url);
            }
            return finalUrl.replace(/\/+$/,'');
        }

        NotInitialised.prototype = Error.prototype;

        function Restable() {
            BCAPIMessages.apply(this);
            BCBelongsToMany.apply(this);

            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#queryDB
             * @methodOf BaseClass.BCRestable
             * @static
             * @description
             * Run a GET request against the API
             *
             * @example
             * <pre>
             * Model.query({category: 'A'}).then(models => {});
             * </pre>
             *
             * @param {Object} params The URL parameters to send with the request
             * @param {boolean} initialise Whether to run data through model constructor or return as raw objects
             * @return {Promise} Resolves with array of models
             * @throws {NotInitialised} When API is not configured
             */
            this.queryDB = function(params, initialise = true) {
                const Class = this;
                if(_.isUndefined(this.api))
                    throw new NotInitialised();
                let url = compileUrl(this.api, params);
                return $http.get(url).then(response => {
                    // check if pagated
                    let data = response.data;
                    if(data.data)
                        data = data.data;

                    this.broadcastAPIMessage(response);
                    if(!initialise)
                        return data;
                    this.cachedQueries.cache(params);
                    return _.map(data, r => Class.new(r));
                }, error => {
                    this.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            };


            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#create
             * @methodOf BaseClass.BCRestable
             * @static
             * @description
             * Initialize a new object and save it immediately
             * @param {Object} attrs Model attributes
             * @return {Promise} Resolves with the newly saved model
             */
            this.create = function(attrs) {
                const Class = this;
                let instance = Class.new(attrs);
                return instance.$save();
            };

            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#queryAdmin
             * @methodOf BaseClass.BCRestable
             * @static
             * @description
             * Run a GET request against the API as an admin
             *
             * @example
             * <pre>
             * Model.queryAdmin('trashed', companyGuid).then(models => {});
             * </pre>
             *
             * @param {Object} params The URL parameters to send with the request
             *
             * Include a status, one of `normal`, `trashed`, `justtrashed`
             * Include a guid of the company to query as
             * @param {boolean} initialise Whether to run data through model constructor or return as raw objects
             * @return {Promise} Resolves with array of models
             * @throws {NotInitialised} When API or this endpoint is not configured
             */
            this.queryAdmin = function(params, initialise = true) {
                const Class = this;
                if(!_.has(this._optionalRoutes,'queryAdmin'))
                    throw new NotInitialised();
                let url = compileUrl(this._optionalRoutes.queryAdmin, params);
                return $http.get(url).then(response => {

                    // check if pagated
                    let data = response.data;
                    if(data.data)
                        data = data.data;

                    this.broadcastAPIMessage(response);
                    if(!initialise)
                        return data;
                    return _.map(data, r => Class.new(r));
                }, error => {
                    this.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            };

            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#getDB
             * @methodOf BaseClass.BCRestable
             * @static
             * @description
             * Run a GET request against the API to retrieve a specific object
             *
             * @example
             * <pre>
             * Model.get({guid: 'A'}).then(model => {});
             * </pre>
             *
             * @param {Object} params The URL parameters to send with the request
             * @param {boolean} initialise Whether to run data through model constructor or return as raw objects
             * @return {Promise} Resolves with single model
             * @throws {NotInitialised} When API is not configured
             */
            this.getDB = function(params = {}, initialise = true) {
                const Class = this;
                if(_.isUndefined(this.api))
                    throw new NotInitialised();
                let url = compileUrl(this.api, params);
                return $http.get(url).then(response => {
                    this.broadcastAPIMessage(response);
                    let result = response.data;
                    if(_.isArray(result))
                        return $log.error('Was expecting a single object, received array instead');
                    if(initialise)
                        return Class.new(result);
                    return result;
                }, error => {
                    this.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            };

            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#configureApi
             * @methodOf BaseClass.BCRestable
             * @static
             * @description
             * Configure API endpoint
             *
             * @param {String} url The URL of the endpoint, may include named parameters (/api/models/:id)
             * @param {Object} optionalRoutes A map of optional routes and their corresponding URLs
             *
             * Available are `$reinstate` and `queryAdmin`
             *
             * @example
             * <pre>
             * Model.configureApi('/api/model/:guid');
             * </pre>
             * @returns {Class} Returns self
             */
            this.configureApi = function(url, optionalRoutes) {
                this.api = url;
                this._optionalRoutes = _.assign({}, optionalRoutes);
                return this;
            };

            /**
             * @ngdoc property
             * @name BaseClass.BCRestable#$dirty
             * @propertyOf BaseClass.BCRestable
             * @description
             * Check whether a model was changed since it last communicated with the server
             *
             * @param {Array<String>} omit Property names to omit from comparison
             * @return {Boolean} True if the model was changed
             */
            helpers.privateVariable(this,'__$dirty',function(omit = []) {
                omit.push('$$hashKey');
                let current = _.cloneDeep(_.omit(this, omit));
                let saved = _.omit(this.$savedState, omit);
                return !_.isEqual(current, saved);
            });

            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#$save
             * @methodOf BaseClass.BCRestable
             * @description
             * Save object to REST API
             *
             * If the instance's primaryKey is set, it'll send a PUT request.
             * Otherwise it'll POST to create a new model. It expects the API
             * to answer with the model data, so it can augment the Instance
             * with a primaryKey.
             *
             * @return {Promise} Resolves the saved object
             * @example
             * <pre>
             * model.$save().then(model => {}, error => {});
             * </pre>
             */
            helpers.privateVariable(this,'__$save',function() {
                if(_.isUndefined(this.constructor.api))
                    throw new NotInitialised();
                let primaryKey = this.constructor.primaryKey;
                let instance = this;
                let url = compileUrl(this.constructor.api, instance);
                let shouldCreate = _.isUndefined(instance[primaryKey]);
                if ( shouldCreate ) {
                    let data = _.defaults({}, this);
                    if($state.params.as)
                        data.company_id = $state.params.as;
                    return $http.post(url, data).then( response => {
                        this.broadcastAPIMessage(response);
                        let updatedData = response.data;
                        _.defaults(instance, updatedData);
                        instance.$savedState = _.cloneDeep(instance);
                        instance.constructor.cache(instance);
                        return instance;
                    }, error => {
                        this.broadcastAPIMessage(error);
                        return $q.reject(error);
                    });
                }
                return $http.put(url, this).then( response => {
                    this.broadcastAPIMessage(response);
                    _.assign(instance, response.data);
                    instance.$savedState = _.cloneDeep(instance);
                    instance.constructor.cache(instance);
                    return instance;
                }, error => {
                    this.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            });

            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#$reinstate
             * @methodOf BaseClass.BCRestable
             * @description
             * Restore a deleted model, requires admin priviledges
             *
             * @param {Object} params Optional URL parameters
             * @example
             * <pre>
             * model.$delete().then(response => {}, error => {});
             * </pre>
             */
            helpers.privateVariable(this,'__$reinstate',function(params) {
                if(!_.has(this.constructor._optionalRoutes,'reinstate'))
                    throw new NotInitialised();
                let url = compileUrl(this.constructor._optionalRoutes.reinstate, this);
                return $http.post(url).then(response => {
                    this.broadcastAPIMessage(response);
                    return response;
                }, error => {
                    this.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            });
            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#$delete
             * @methodOf BaseClass.BCRestable
             * @description
             * Delete object on REST API
             *
             * @param {Object} params Optional URL parameters
             * @example
             * <pre>
             * model.$delete().then(response => {}, error => {});
             * </pre>
             */
            helpers.privateVariable(this,'__$delete',function(params) {
                if(_.isUndefined(this.constructor.api))
                    throw new NotInitialised();
                this.constructor.cached.remove(this[this.constructor.primaryKey]);
                let url = compileUrl(this.constructor.api, this);
                return $http.delete(url).then(response => {
                    this.broadcastAPIMessage(response);
                    return response;
                }, error => {
                    this.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            });
            /**
             * @ngdoc method
             * @name BaseClass.BCRestable#$compileUrl
             * @methodOf BaseClass.BCRestable
             * @description
             * Compile the url for the current resource instance
             *
             * @example
             * <pre>
             * model.$compileUrl()
             * </pre>
             */
            helpers.privateVariable(this,'__$compileUrl',function(params) {
                if(_.isUndefined(this.constructor.api)) {
                    throw new NotInitialised();
                }

                return compileUrl(this.constructor.api, this);
            });
        }
        return Restable;
    }).name;
